import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import PdfReaderCanvas from "@/components/PdfReaderCanvas";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BookOpen,
  Award,
  Sparkles,
  Trophy,
  CheckCircle2,
  Clock,
  Download,
  PlusCircle,
  ShieldCheck,
  Truck,
  Eye,
  Trash2,
  Flame,
  Star,
  Users,
  Compass,
  FileCheck,
  ChevronLeft,
  ChevronRight,
  FileText,
  Loader2,
  CheckSquare,
  Square,
  Maximize2,
  X,
} from "lucide-react";

const CATEGORIAS = ["Operaciones", "OEA", "Seguridad Integral", "Políticas y Conducta", "Calidad", "Tecnología"];

export default function ManualesPage() {
  const { user } = useAuth();
  const isHRorAdmin = user?.rol === "administrador" || user?.rol === "rrhh";

  const [manuales, setManuales] = useState([]);
  const [progreso, setProgreso] = useState(null);
  const [metricas, setMetricas] = useState(null);
  const [selectedCat, setSelectedCat] = useState("todos");
  const [loading, setLoading] = useState(true);

  // Lector Interactivo Paso a Paso
  const [selectedManual, setSelectedManual] = useState(null);
  const [isReadOpen, setIsReadOpen] = useState(false);
  const [readerStep, setReaderStep] = useState(1);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [checklist, setChecklist] = useState({ p1: false, p2: false, p3: false });

  // Modal Crear Manual (RRHH)
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newManual, setNewManual] = useState({
    titulo: "",
    categoria: "Operaciones",
    descripcion: "",
    version: "1.0",
    puntos_xp: 100,
    tiempo_lectura_minutos: 15,
  });
  const [manualFile, setManualFile] = useState(null);
  const [submittingManual, setSubmittingManual] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [manRes, progRes] = await Promise.all([
        api.getManuales(),
        api.getMiProgresoManuales(),
      ]);
      setManuales(manRes.data || []);
      setProgreso(progRes.data || null);

      if (isHRorAdmin) {
        const metRes = await api.getMetricasManuales();
        setMetricas(metRes.data || null);
      }
    } catch (err) {
      toast.error("Error al cargar manuales corporativos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [isHRorAdmin]);

  const handleOpenReader = async (manual) => {
    setSelectedManual(manual);
    setReaderStep(1);
    setChecklist({ p1: false, p2: false, p3: false });
    setIsReadOpen(true);

    if (manual.tiene_archivo) {
      setLoadingPdf(true);
      try {
        const res = await api.downloadManual(manual.id);
        const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        setPdfBlobUrl(url);
      } catch (err) {
        toast.error("No se pudo cargar el archivo PDF interactivo");
      } finally {
        setLoadingPdf(false);
      }
    } else {
      setPdfBlobUrl(null);
    }
  };

  const handleCloseReader = () => {
    setIsReadOpen(false);
    if (pdfBlobUrl) {
      window.URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
  };

  const handleCompleteManual = async (manualId) => {
    setCompleting(true);
    try {
      const res = await api.completarManual(manualId);
      toast.success(res.data.message || "¡Manual completado con éxito!", {
        description: res.data.xp_ganados ? `Has ganado +${res.data.xp_ganados} XP para tu nivel corporativo.` : undefined,
      });
      handleCloseReader();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrar la lectura del manual");
    } finally {
      setCompleting(false);
    }
  };

  const handleDownloadDirect = async (id, titulo) => {
    try {
      const res = await api.downloadManual(id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${titulo.replace(/ /g, "_")}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Archivo descargado");
    } catch (err) {
      toast.error("Error al descargar el archivo del manual");
    }
  };

  const handleCreateManual = async (e) => {
    e.preventDefault();
    if (!newManual.titulo || !newManual.categoria) {
      toast.error("Completa el título y la categoría");
      return;
    }
    setSubmittingManual(true);
    try {
      const formData = new FormData();
      formData.append("titulo", newManual.titulo);
      formData.append("categoria", newManual.categoria);
      formData.append("descripcion", newManual.descripcion);
      formData.append("version", newManual.version);
      formData.append("puntos_xp", newManual.puntos_xp.toString());
      formData.append("tiempo_lectura_minutos", newManual.tiempo_lectura_minutos.toString());
      if (manualFile) formData.append("archivo", manualFile);

      await api.createManual(formData);
      toast.success("Manual corporativo publicado exitosamente");
      setIsCreateOpen(false);
      setNewManual({
        titulo: "",
        categoria: "Operaciones",
        descripcion: "",
        version: "1.0",
        puntos_xp: 100,
        tiempo_lectura_minutos: 15,
      });
      setManualFile(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear manual");
    } finally {
      setSubmittingManual(false);
    }
  };

  const handleDeleteManual = async (id) => {
    if (!window.confirm("¿Seguro de desactivar este manual?")) return;
    try {
      await api.deleteManual(id);
      toast.success("Manual desactivado");
      loadData();
    } catch (err) {
      toast.error("Error al desactivar manual");
    }
  };

  const filteredManuales = manuales.filter((m) => {
    return selectedCat === "todos" || m.categoria === selectedCat;
  });

  const totalSteps = 4;
  const progressPct = Math.round((readerStep / totalSteps) * 100);

  return (
    <div className="space-y-6">
      {/* Header Limpio con Widget de Nivel Actual */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-sm">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2.5">
            <div className="p-2 bg-orange-100 text-orange-600 rounded-xl">
              <BookOpen className="w-5 h-5" />
            </div>
            Manuales y Políticas Corporativas
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Lee de forma interactiva y acredita tus conocimientos en los lineamientos oficiales de CPS Logistics.
          </p>
        </div>

        {/* Nivel Actual Compacto y Elegante */}
        <div className="flex items-center gap-3.5 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200/80 p-3 px-4 rounded-xl min-w-[280px]">
          <div className="w-10 h-10 rounded-xl bg-orange-600 text-white flex items-center justify-center font-extrabold text-base shadow-sm shrink-0">
            {progreso?.nivel || 1}
          </div>
          <div className="flex-1 space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="font-bold text-slate-800">{progreso?.titulo_nivel || "Novato Aprendiz"}</span>
              <span className="font-extrabold text-orange-600 flex items-center gap-1">
                <Flame className="w-3.5 h-3.5 fill-orange-500 text-orange-500" />
                {progreso?.total_xp || 0} XP
              </span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-1.5 overflow-hidden">
              <div
                className="bg-orange-600 h-full rounded-full transition-all duration-500"
                style={{ width: `${progreso?.progreso_nivel_pct || 0}%` }}
              />
            </div>
            <div className="text-[10px] text-slate-500 flex justify-between">
              <span>Nivel Actual</span>
              <span>{progreso?.progreso_nivel_pct || 0}% al sig. nivel</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="catalogo" className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <TabsList className="bg-slate-100 p-1 rounded-xl">
            <TabsTrigger value="catalogo" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
              Catálogo de Manuales ({manuales.length})
            </TabsTrigger>
            {isHRorAdmin && (
              <TabsTrigger value="gestion-rrhh" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
                Panel RRHH & Cumplimiento
              </TabsTrigger>
            )}
          </TabsList>

          {isHRorAdmin && (
            <Button
              onClick={() => setIsCreateOpen(true)}
              className="bg-orange-600 hover:bg-orange-700 text-white shadow-sm flex items-center gap-2"
            >
              <PlusCircle className="w-4 h-4" />
              Nuevo Manual
            </Button>
          )}
        </div>

        {/* Tab: Catálogo de Manuales */}
        <TabsContent value="catalogo" className="space-y-6">
          {/* Category Chips */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
            <Button
              size="sm"
              variant={selectedCat === "todos" ? "default" : "outline"}
              onClick={() => setSelectedCat("todos")}
              className={selectedCat === "todos" ? "bg-slate-900 text-white" : "border-slate-200 text-slate-600"}
            >
              Todos los Manuales
            </Button>
            {CATEGORIAS.map((cat) => (
              <Button
                key={cat}
                size="sm"
                variant={selectedCat === cat ? "default" : "outline"}
                onClick={() => setSelectedCat(cat)}
                className={selectedCat === cat ? "bg-slate-900 text-white" : "border-slate-200 text-slate-600"}
              >
                {cat}
              </Button>
            ))}
          </div>

          {/* Grid de Manuales */}
          {loading ? (
            <div className="text-center py-12 text-slate-500">Cargando manuales corporativos...</div>
          ) : filteredManuales.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center space-y-3">
                <BookOpen className="w-12 h-12 text-slate-300 mx-auto" />
                <h3 className="font-semibold text-slate-700 text-lg">No hay manuales en esta categoría</h3>
                <p className="text-sm text-slate-500 max-w-sm mx-auto">
                  El equipo de Recursos Humanos publicará nuevos contenidos de formación próximamente.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredManuales.map((m) => (
                <Card
                  key={m.id}
                  className={`border overflow-hidden transition-all duration-200 hover:shadow-lg ${
                    m.completado ? "border-emerald-200 bg-emerald-50/20" : "border-slate-200 bg-white"
                  }`}
                >
                  <CardHeader className="pb-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <Badge variant="outline" className="text-xs bg-slate-100 text-slate-700 border-slate-200">
                        {m.categoria}
                      </Badge>
                      {m.completado ? (
                        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 flex items-center gap-1">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          Completado
                        </Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-800 border-amber-300 flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          Pendiente
                        </Badge>
                      )}
                    </div>
                    <CardTitle className="text-lg font-bold text-slate-900 leading-snug line-clamp-2">
                      {m.titulo}
                    </CardTitle>
                    <CardDescription className="text-xs text-slate-500 line-clamp-2">
                      {m.descripcion || "Manual oficial de lineamientos, procesos y estándares de CPS Logistics."}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="space-y-4 pt-1">
                    <div className="grid grid-cols-2 gap-2 text-xs bg-slate-50 p-2.5 rounded-xl border border-slate-100 text-slate-600">
                      <div className="flex items-center gap-1.5">
                        <Flame className="w-4 h-4 text-orange-500" />
                        <span className="font-semibold text-slate-800">+{m.puntos_xp} XP</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Clock className="w-4 h-4 text-slate-400" />
                        <span>~{m.tiempo_lectura_minutos} min</span>
                      </div>
                      <div className="text-slate-400">Versión: {m.version}</div>
                      <div className="text-slate-400 truncate">Pub: {m.fecha_creacion}</div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleOpenReader(m)}
                        className={`flex-1 flex items-center justify-center gap-2 ${
                          m.completado ? "bg-slate-800 hover:bg-slate-900 text-white" : "bg-orange-600 hover:bg-orange-700 text-white shadow-sm"
                        }`}
                        size="sm"
                      >
                        <Eye className="w-4 h-4" />
                        {m.completado ? "Repasar Manual" : "Leer Manual Interactivo"}
                      </Button>
                      {m.tiene_archivo && (
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => handleDownloadDirect(m.id, m.titulo)}
                          className="border-slate-200 hover:bg-slate-100 shrink-0"
                          title="Descargar copia PDF"
                        >
                          <Download className="w-4 h-4 text-slate-600" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Tab: Panel RRHH & Cumplimiento */}
        {isHRorAdmin && (
          <TabsContent value="gestion-rrhh" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Cumplimiento por Manual */}
              <Card className="lg:col-span-2 border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-base font-bold text-slate-800 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    Índice de Cumplimiento por Manual
                  </CardTitle>
                  <CardDescription>
                    Porcentaje de colaboradores que han completado la lectura oficial de cada manual.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {metricas?.manuales?.map((man) => (
                    <div key={man.id} className="space-y-1.5 p-3 rounded-xl bg-slate-50 border border-slate-100">
                      <div className="flex items-center justify-between text-sm">
                        <div className="font-semibold text-slate-800 flex items-center gap-2">
                          <span>{man.titulo}</span>
                          <Badge variant="outline" className="text-[10px] text-slate-500">
                            {man.categoria}
                          </Badge>
                        </div>
                        <div className="text-right">
                          <span className="font-bold text-emerald-700">{man.pct_cumplimiento}%</span>
                          <span className="text-xs text-slate-400 ml-1">({man.completados}/{metricas?.total_empleados})</span>
                        </div>
                      </div>
                      <Progress value={man.pct_cumplimiento} className="h-2" />
                      <div className="flex justify-end pt-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteManual(man.id)}
                          className="text-xs text-rose-600 hover:bg-rose-50 h-7 px-2"
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> Desactivar
                        </Button>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Leaderboard XP */}
              <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-base font-bold text-slate-800 flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-amber-500" />
                    Top Lectores de la Empresa
                  </CardTitle>
                  <CardDescription>Colaboradores con mayor avance de capacitación.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {metricas?.leaderboard?.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2.5 rounded-xl bg-slate-50 border border-slate-100"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-6 h-6 rounded-full flex items-center justify-center font-bold text-xs ${
                            index === 0
                              ? "bg-amber-400 text-slate-900"
                              : index === 1
                              ? "bg-slate-300 text-slate-800"
                              : index === 2
                              ? "bg-amber-700 text-white"
                              : "bg-slate-200 text-slate-600"
                          }`}
                        >
                          {index + 1}
                        </div>
                        <div>
                          <div className="text-xs font-semibold text-slate-800 leading-tight">{item.nombre}</div>
                          <div className="text-[10px] text-slate-400">{item.departamento}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs font-extrabold text-orange-600">{item.total_xp} XP</div>
                        <div className="text-[10px] text-slate-400">{item.manuales_leidos} manuales</div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* LECTOR INTERACTIVO DE MANUALES (MODAL PANTALLA COMPLETA PÁGINA POR PÁGINA) */}
      <Dialog open={isReadOpen} onOpenChange={handleCloseReader}>
        <DialogContent className="max-w-6xl w-[96vw] h-[92vh] flex flex-col p-0 overflow-hidden bg-slate-50 shadow-2xl">
          {/* Header Superior del Lector */}
          <div className="p-4 sm:px-6 bg-white border-b border-slate-200 flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 overflow-hidden">
                <Badge className="bg-orange-100 text-orange-800 border-none shrink-0">
                  {selectedManual?.categoria}
                </Badge>
                <h3 className="font-bold text-slate-900 text-sm sm:text-base truncate">
                  {selectedManual?.titulo}
                </h3>
                <span className="text-xs text-slate-400 hidden sm:inline">v{selectedManual?.version}</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs font-semibold text-orange-600 border-orange-200 bg-orange-50">
                  +{selectedManual?.puntos_xp} XP
                </Badge>
                <Button variant="ghost" size="icon" onClick={handleCloseReader} className="h-8 w-8 text-slate-500 hover:text-slate-800">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Barra de progreso interactiva paso a paso */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span className="font-medium text-slate-700">
                  Paso {readerStep} de {totalSteps}:{" "}
                  {readerStep === 1 && "Ficha y Objetivos"}
                  {readerStep === 2 && "Lectura Oficial del Documento"}
                  {readerStep === 3 && "Puntos de Control & Comprensión"}
                  {readerStep === 4 && "Certificación y Reclamo de XP"}
                </span>
                <span className="font-bold text-orange-600">{progressPct}%</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                <div
                  className="bg-orange-600 h-full rounded-full transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
          </div>

          {/* Cuerpo Central del Lector (Páginas Interactivas) */}
          <div className={`flex-1 bg-slate-50 flex flex-col ${readerStep === 2 ? "p-1 sm:p-2 overflow-hidden" : "p-3 sm:p-5 overflow-y-auto"}`}>
            {/* PÁGINA 1: RESUMEN Y OBJETIVOS */}
            {readerStep === 1 && (
              <div className="max-w-2xl mx-auto space-y-6 py-4">
                <div className="p-6 bg-white rounded-2xl border border-slate-200 shadow-sm space-y-4">
                  <div className="w-12 h-12 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center">
                    <Compass className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">{selectedManual?.titulo}</h2>
                    <p className="text-sm text-slate-500 mt-1">
                      {selectedManual?.descripcion || "Lineamientos y procedimientos obligatorios establecidos para el personal de CPS Logistics."}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-2">
                    <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                      <div className="text-[11px] text-slate-400 font-medium">Recompensa</div>
                      <div className="text-sm font-bold text-orange-600 flex items-center gap-1 mt-0.5">
                        <Flame className="w-4 h-4 fill-orange-500" />
                        +{selectedManual?.puntos_xp} XP
                      </div>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                      <div className="text-[11px] text-slate-400 font-medium">Tiempo Estimado</div>
                      <div className="text-sm font-semibold text-slate-700 flex items-center gap-1 mt-0.5">
                        <Clock className="w-4 h-4 text-slate-400" />
                        ~{selectedManual?.tiempo_lectura_minutos} min
                      </div>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                      <div className="text-[11px] text-slate-400 font-medium">Versión Oficial</div>
                      <div className="text-sm font-semibold text-slate-700 mt-0.5">
                        v{selectedManual?.version}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-5 bg-white rounded-2xl border border-slate-200 space-y-3">
                  <h4 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-amber-500" />
                    Objetivos de Aprendizaje
                  </h4>
                  <ul className="text-xs text-slate-600 space-y-2 list-disc list-inside leading-relaxed">
                    <li>Conocer las normativas y procedimientos operativos vigentes en la organización.</li>
                    <li>Estandarizar las mejores prácticas y lineamientos de seguridad y cumplimiento.</li>
                    <li>Acreditar tu lectura institucional para sumar puntos de experiencia en tu perfil.</li>
                  </ul>
                </div>

                <div className="text-center pt-2">
                  <Button
                    onClick={() => setReaderStep(2)}
                    className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-2.5 rounded-xl shadow-md font-medium"
                  >
                    Comenzar Lectura Oficial
                    <ChevronRight className="w-4 h-4 ml-1.5" />
                  </Button>
                </div>
              </div>
            )}

            {/* PÁGINA 2: LECTURA OFICIAL DEL DOCUMENTO (PDF EN VIVO CON VISOR INTERACTIVO NATIVO CANVAS) */}
            {readerStep === 2 && (
              <div className="flex-1 flex flex-col h-full min-h-[480px]">
                {selectedManual?.tiene_archivo ? (
                  loadingPdf ? (
                    <div className="flex-1 min-h-[450px] flex flex-col items-center justify-center bg-white rounded-2xl border border-slate-200 space-y-3">
                      <Loader2 className="w-9 h-9 text-orange-500 animate-spin" />
                      <p className="text-sm text-slate-600 font-semibold">Cargando documento oficial...</p>
                      <p className="text-xs text-slate-400">Preparando lector interactivo página por página</p>
                    </div>
                  ) : pdfBlobUrl ? (
                    <PdfReaderCanvas
                      pdfBlobUrl={pdfBlobUrl}
                      documentTitle={selectedManual?.titulo}
                      onFinishedReading={() => {}}
                      onDownload={() => handleDownloadDirect(selectedManual?.id, selectedManual?.titulo)}
                    />
                  ) : (
                    <div className="p-8 text-center bg-white rounded-2xl border border-slate-200">
                      <p className="text-slate-500">No se pudo cargar el archivo para este manual.</p>
                    </div>
                  )
                ) : (
                  <div className="max-w-2xl mx-auto p-8 bg-white rounded-2xl border border-slate-200 space-y-4 text-slate-700 leading-relaxed text-sm my-auto">
                    <h3 className="font-bold text-lg text-slate-900">{selectedManual?.titulo}</h3>
                    <p>{selectedManual?.descripcion}</p>
                    <div className="p-4 bg-orange-50 rounded-xl border border-orange-200 text-xs text-orange-800">
                      Este documento contiene lineamientos normativos directos de cumplimiento obligatorio.
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* PÁGINA 3: VALIDACIÓN Y CHECKLIST DE COMPRENSIÓN */}
            {readerStep === 3 && (
              <div className="max-w-2xl mx-auto space-y-6 py-4">
                <div className="p-6 bg-white rounded-2xl border border-slate-200 shadow-sm space-y-4">
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center">
                    <ShieldCheck className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">Validación de Comprensión y Compromiso</h2>
                    <p className="text-xs text-slate-500 mt-1">
                      Marca cada uno de los puntos a continuación para certificar tu lectura responsable.
                    </p>
                  </div>

                  <div className="space-y-3 pt-2">
                    <div
                      onClick={() => setChecklist({ ...checklist, p1: !checklist.p1 })}
                      className={`p-4 rounded-xl border cursor-pointer transition-all flex items-start gap-3 ${
                        checklist.p1 ? "bg-emerald-50/50 border-emerald-300 text-slate-900" : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100/50"
                      }`}
                    >
                      {checklist.p1 ? (
                        <CheckSquare className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                      ) : (
                        <Square className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
                      )}
                      <div className="text-xs leading-relaxed">
                        <span className="font-semibold block text-slate-800">Lectura Íntegra</span>
                        Confirmo que he leído y revisado en su totalidad los procesos y normas descritos en este manual.
                      </div>
                    </div>

                    <div
                      onClick={() => setChecklist({ ...checklist, p2: !checklist.p2 })}
                      className={`p-4 rounded-xl border cursor-pointer transition-all flex items-start gap-3 ${
                        checklist.p2 ? "bg-emerald-50/50 border-emerald-300 text-slate-900" : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100/50"
                      }`}
                    >
                      {checklist.p2 ? (
                        <CheckSquare className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                      ) : (
                        <Square className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
                      )}
                      <div className="text-xs leading-relaxed">
                        <span className="font-semibold block text-slate-800">Aplicación en mis Labores</span>
                        Me comprometo a aplicar diligentemente los estándares y protocolos en mis tareas diarias en la empresa.
                      </div>
                    </div>

                    <div
                      onClick={() => setChecklist({ ...checklist, p3: !checklist.p3 })}
                      className={`p-4 rounded-xl border cursor-pointer transition-all flex items-start gap-3 ${
                        checklist.p3 ? "bg-emerald-50/50 border-emerald-300 text-slate-900" : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100/50"
                      }`}
                    >
                      {checklist.p3 ? (
                        <CheckSquare className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                      ) : (
                        <Square className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
                      )}
                      <div className="text-xs leading-relaxed">
                        <span className="font-semibold block text-slate-800">Cumplimiento Organizacional</span>
                        Entiendo que el cumplimiento de estas disposiciones contribuye a la seguridad y excelencia de CPS Logistics.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* PÁGINA 4: FINALIZACIÓN Y CERTIFICACIÓN */}
            {readerStep === 4 && (
              <div className="max-w-xl mx-auto space-y-6 py-6 text-center">
                <div className="p-8 bg-white rounded-3xl border border-slate-200 shadow-lg space-y-5">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-tr from-amber-400 to-orange-500 text-white flex items-center justify-center mx-auto shadow-md shadow-orange-500/20">
                    <Award className="w-10 h-10" />
                  </div>

                  <div className="space-y-1">
                    <span className="text-xs font-bold text-orange-600 uppercase tracking-widest">
                      Capacitación Oficial
                    </span>
                    <h2 className="text-2xl font-extrabold text-slate-900">
                      ¡Lectura Completada con Éxito!
                    </h2>
                    <p className="text-xs text-slate-500 max-w-sm mx-auto">
                      Has recorrido todos los apartados de <strong>{selectedManual?.titulo}</strong>.
                    </p>
                  </div>

                  <div className="p-4 bg-orange-50 rounded-2xl border border-orange-200 max-w-xs mx-auto">
                    <div className="text-xs text-orange-800 font-semibold">Recompensa Otorgada</div>
                    <div className="text-2xl font-black text-orange-600 flex items-center justify-center gap-1.5 mt-1">
                      <Flame className="w-6 h-6 fill-orange-500 text-orange-500" />
                      +{selectedManual?.puntos_xp} Puntos XP
                    </div>
                  </div>

                  {selectedManual?.completado ? (
                    <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-200 text-xs text-emerald-800 font-semibold flex items-center justify-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      Ya habías acreditado este manual {selectedManual.fecha_completado ? `el ${selectedManual.fecha_completado}` : ""}
                    </div>
                  ) : (
                    <div className="pt-2">
                      <Button
                        onClick={() => handleCompleteManual(selectedManual.id)}
                        disabled={completing}
                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-base py-6 rounded-2xl shadow-lg shadow-emerald-600/20 font-bold"
                      >
                        {completing ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin mr-2" />
                            Acreditando lectura...
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="w-5 h-5 mr-2" />
                            Confirmar Lectura y Reclamar +{selectedManual?.puntos_xp} XP
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer de Navegación del Lector de Manual */}
          <div className="p-3.5 sm:px-6 bg-white border-t border-slate-200 flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              disabled={readerStep === 1}
              onClick={() => setReaderStep((prev) => Math.max(1, prev - 1))}
              className="text-xs border-slate-200"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Paso Anterior
            </Button>

            {/* Dots e indicador del paso del asistente */}
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-semibold text-slate-500 hidden sm:inline">
                Paso {readerStep} de {totalSteps}
              </span>
              <div className="flex items-center gap-1.5">
                {[1, 2, 3, 4].map((step) => (
                  <button
                    key={step}
                    onClick={() => setReaderStep(step)}
                    className={`h-2 rounded-full transition-all ${
                      readerStep === step
                        ? "bg-orange-600 w-5"
                        : step < readerStep
                        ? "bg-emerald-500 w-2"
                        : "bg-slate-200 w-2"
                    }`}
                    title={`Paso ${step}`}
                  />
                ))}
              </div>
            </div>

            {/* Botón contextual del paso actual */}
            {readerStep === 1 && (
              <Button
                size="sm"
                onClick={() => setReaderStep(2)}
                className="text-xs bg-orange-600 hover:bg-orange-700 text-white shadow-sm font-semibold gap-1"
              >
                Comenzar Lectura
                <ChevronRight className="w-4 h-4" />
              </Button>
            )}
            {readerStep === 2 && (
              <Button
                size="sm"
                onClick={() => setReaderStep(3)}
                className="text-xs bg-orange-600 hover:bg-orange-700 text-white shadow-sm font-semibold gap-1"
              >
                Continuar a Validación
                <ChevronRight className="w-4 h-4" />
              </Button>
            )}
            {readerStep === 3 && (
              <Button
                size="sm"
                onClick={() => setReaderStep(4)}
                className="text-xs bg-orange-600 hover:bg-orange-700 text-white shadow-sm font-semibold gap-1"
              >
                Avanzar a Reclamar XP
                <ChevronRight className="w-4 h-4" />
              </Button>
            )}
            {readerStep === 4 && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleCloseReader}
                className="text-xs border-slate-300 font-medium"
              >
                Cerrar Visor
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: Crear Nuevo Manual (RRHH) */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <PlusCircle className="w-5 h-5" />
              Publicar Nuevo Manual Corporativo
            </DialogTitle>
            <DialogDescription>
              Configura el manual para que esté disponible para todo el personal con lectura interactiva.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateManual} className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="titulo">Título del Manual *</Label>
              <Input
                id="titulo"
                placeholder="Ej. Manual de Operaciones Logísticas, Manual OEA..."
                value={newManual.titulo}
                onChange={(e) => setNewManual({ ...newManual, titulo: e.target.value })}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="categoria">Categoría *</Label>
                <Select
                  value={newManual.categoria}
                  onValueChange={(val) => setNewManual({ ...newManual, categoria: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIAS.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="version">Versión *</Label>
                <Input
                  id="version"
                  placeholder="1.0"
                  value={newManual.version}
                  onChange={(e) => setNewManual({ ...newManual, version: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="puntos_xp">Puntos XP Otorgados *</Label>
                <Input
                  id="puntos_xp"
                  type="number"
                  min="10"
                  step="10"
                  value={newManual.puntos_xp}
                  onChange={(e) => setNewManual({ ...newManual, puntos_xp: parseInt(e.target.value) || 0 })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tiempo">Tiempo Estimado (Minutos) *</Label>
                <Input
                  id="tiempo"
                  type="number"
                  min="5"
                  value={newManual.tiempo_lectura_minutos}
                  onChange={(e) => setNewManual({ ...newManual, tiempo_lectura_minutos: parseInt(e.target.value) || 0 })}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="desc">Descripción / Resumen</Label>
              <Textarea
                id="desc"
                placeholder="Breve resumen del contenido y alcance del manual..."
                value={newManual.descripcion}
                onChange={(e) => setNewManual({ ...newManual, descripcion: e.target.value })}
                rows={3}
              />
            </div>

            <div className="space-y-2 bg-slate-50 p-3 rounded-lg border border-slate-200">
              <Label htmlFor="man_file" className="text-xs font-semibold text-slate-700 block">
                Archivo PDF del Manual *
              </Label>
              <Input
                id="man_file"
                type="file"
                accept=".pdf"
                onChange={(e) => setManualFile(e.target.files?.[0] || null)}
                className="bg-white text-xs"
              />
            </div>

            <div className="pt-2 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={submittingManual} className="bg-orange-600 hover:bg-orange-700 text-white">
                {submittingManual ? "Publicando..." : "Publicar Manual"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
